import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Pressable, View } from "@-/com.npmjs.react-native";

/**
 * In-content back chevron for screens whose "back" means popping their OWN navigator, and which
 * therefore never publish a `PrimaryControlsStripBack` entry (see `useImmediateNavigatorOnBack`).
 *
 * `inset` controls the built-in offset. Callers that place this in a bare content column keep the
 * default `"content"` inset; callers that already own an aligned lane — the morpheme-panels
 * toolbar, whose lane centres the chevron against the More button — pass `"none"` so the lane's
 * alignment is not fought by a margin baked into this component. The offset used to be an
 * unconditional `mt-2 ml-2` that every lane had to compensate for.
 *
 * `onBack` is required: a caller without a back action doesn't render this component at all.
 */
export function CardBackButtonContainer({
  inset = "content",
  onBack,
}: {
  inset?: "content" | "none";
  onBack: () => void;
}) {
  return (
    <View className="shrink-0 self-start" pointerEvents="box-none">
      <Pressable
        accessibilityLabel="Back"
        accessibilityRole="button"
        className={inset === "content" ? "mt-2 ml-2" : undefined}
        hitSlop={10}
        onPress={onBack}
        // Stable id for E2E: on the release build the "Back" a11y *text* label is
        // intermittently missing from the hierarchy while the card-flip animation settles, so
        // a text `tapOn: "Back"` can't find it; the testID (iOS accessibilityIdentifier /
        // Android resource-id) is present as soon as the chevron mounts.
        testID="card-back-button"
      >
        <Icon className="text-muted-foreground" name="FiChevronLeftBold" size={24} />
      </Pressable>
    </View>
  );
}
